import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
// material-ui
import { styled, useTheme } from '@mui/material/styles';
import { Avatar, Box, Grid, Typography, Stack, Tooltip, IconButton, LinearProgress, Chip, Fade } from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import TotalIncomeCard from 'ui-component/cards/Skeleton/TotalIncomeCard';

// assets
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import TodayIcon from '@mui/icons-material/Today';
import UpcomingIcon from '@mui/icons-material/Upcoming';
import PendingActionsIcon from '@mui/icons-material/PendingActions';

// ==============================|| STYLED COMPONENTS ||============================== //

const ProfessionalCardWrapper = styled(MainCard)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.primary,
  overflow: 'hidden',
  position: 'relative',
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '12px',
  boxShadow: '0 2px 10px 0 rgba(0, 0, 0, 0.03)',
  transition: 'all 0.3s ease',
  minHeight: '160px',
  maxHeight: '160px',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px 0 rgba(0, 0, 0, 0.08)',
    borderColor: '#0f766e' // Dark teal accent
  }
}));

const StatusChip = styled(Chip)(({ theme, status }) => ({
  height: 24,
  fontSize: '0.75rem',
  fontWeight: 600,
  backgroundColor:
    status === 'today'
      ? 'rgba(245, 124, 0, 0.1)'
      : status === 'upcoming'
      ? 'rgba(33, 150, 243, 0.1)'
      : status === 'pending'
      ? 'rgba(211, 47, 47, 0.1)'
      : theme.palette.action.selected,
  color: status === 'today' ? '#f57c00' : status === 'upcoming' ? '#1976d2' : status === 'pending' ? '#d32f2f' : theme.palette.text.secondary,
  border: 'none',
  '& .MuiChip-icon': {
    fontSize: '0.875rem',
    color: 'inherit'
  }
}));

// ==============================|| UTILITIES ||============================== //

const calculateReservationStats = (reservationData) => {
  if (!reservationData || reservationData.length === 0) {
    return {
      total: 0,
      todayCheckIns: 0,
      todayCheckOuts: 0,
      upcoming: 0,
      pending: 0,
      confirmed: 0,
      partialStays: 0,
      nightStays: 0,
      revenueToday: 0
    };
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const stats = {
    total: reservationData.length,
    todayCheckIns: 0,
    todayCheckOuts: 0,
    upcoming: 0,
    pending: 0,
    confirmed: 0,
    partialStays: 0,
    nightStays: 0,
    revenueToday: 0
  };

  reservationData.forEach((reservation) => {
    const checkInDate = new Date(reservation.checkInDate);
    const checkOutDate = new Date(reservation.checkOutDate);

    // Check-ins de hoy
    if (checkInDate >= today && checkInDate < tomorrow) {
      stats.todayCheckIns++;
      stats.revenueToday += parseFloat(reservation.totalAmount || 0);
    }

    // Check-outs de hoy
    if (checkOutDate >= today && checkOutDate < tomorrow) {
      stats.todayCheckOuts++;
    }

    // Próximas reservas
    if (checkInDate > tomorrow) {
      stats.upcoming++;
    }

    // Estados
    if (reservation.status === 'pending' || reservation.paymentStatus === 'pending') {
      stats.pending++;
    } else if (reservation.status === 'confirmed') {
      stats.confirmed++;
    }

    // Tipos de estadía
    if (reservation.stayType === 'partial') {
      stats.partialStays++;
    } else {
      stats.nightStays++;
    }
  });

  return stats;
};

// ==============================|| COMPONENTE PRINCIPAL ||============================== //

const TotalReservationCard = ({ isLoading, reservationData }) => {
  const theme = useTheme();
  const [stats, setStats] = useState({
    total: 0,
    todayCheckIns: 0,
    todayCheckOuts: 0,
    upcoming: 0,
    pending: 0,
    confirmed: 0,
    partialStays: 0,
    nightStays: 0,
    revenueToday: 0
  });
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (reservationData) {
      setStats(calculateReservationStats(reservationData));
    }
  }, [reservationData]);

  const confirmationRate = stats.total > 0 ? ((stats.confirmed / stats.total) * 100).toFixed(1) : 0;

  if (isLoading) {
    return <TotalIncomeCard />;
  }

  return (
    <Fade in={true} timeout={500}>
      <ProfessionalCardWrapper border={false} content={false}>
        <Box sx={{ p: 2.5, position: 'relative', height: '100%' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" height="100%">
            <Box flex={1}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  mb: 0.5
                }}
              >
                Reservaciones Activas
              </Typography>

              <Stack direction="row" alignItems="baseline" spacing={1} mb={0.5}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 700,
                    fontSize: '2rem',
                    lineHeight: 1,
                    color: theme.palette.text.primary,
                    letterSpacing: '-0.5px'
                  }}
                >
                  {stats.total}
                </Typography>

                {stats.pending > 0 && (
                  <StatusChip icon={<PendingActionsIcon />} label={`${stats.pending} pendientes`} size="small" status="pending" />
                )}
              </Stack>

              {/* Actividad de hoy */}
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 500
                }}
              >
                <TodayIcon sx={{ fontSize: 14, mr: 0.5, color: '#0f766e' }} />
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600, mr: 0.5 }}>
                  {stats.todayCheckIns}
                </Box> 
                in · 
                <Box component="span" sx={{ color: theme.palette.text.primary, fontWeight: 600, mx: 0.5 }}>
                  {stats.todayCheckOuts}
                </Box> 
                out
              </Typography>

              {/* Barra de confirmación */}
              <Box sx={{ mt: 1.5 }}>
                <Stack spacing={0.5}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.75rem',
                        fontWeight: 500
                      }}
                    >
                      Tasa Confirmación
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 700,
                        fontSize: '0.75rem'
                      }}
                    >
                      {stats.confirmed} <span style={{ color: theme.palette.text.secondary, fontWeight: 500 }}>({confirmationRate}%)</span>
                    </Typography>
                  </Stack>

                  <LinearProgress
                    variant="determinate"
                    value={parseFloat(confirmationRate)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: '#0f766e' // teal
                      }
                    }}
                  />
                </Stack>
              </Box>
            </Box>

            {/* Avatar/Icono */}
            <Avatar
              sx={{
                width: 48,
                height: 48,
                backgroundColor: 'rgba(15, 118, 110, 0.08)',
                color: '#0f766e',
                borderRadius: '12px',
                ml: 1
              }}
            >
              <CalendarMonthIcon sx={{ fontSize: 24 }} />
            </Avatar>
          </Stack>
        </Box>
      </ProfessionalCardWrapper>
    </Fade>
  );
};

TotalReservationCard.propTypes = {
  isLoading: PropTypes.bool,
  reservationData: PropTypes.array
};

export default TotalReservationCard;
